import { useCallback, useEffect, useState } from "react";

import SaveIcon from "@mui/icons-material/Save";
import { Box, Button, CircularProgress, Paper, Tab, Tabs } from "@mui/material";
import { PageContainer } from "@/features/ui";

import {
  AccountingActionCategory,
  AccountingActionCategoryLabel,
  AccountingActionLabel,
  ActionSectionMapping,
  REQUIRED_ACTIONS,
} from "../features/chart-of-account-preferences";
import { PreferenceTab } from "../features/chart-of-account-preferences/preference-tab";
import { useAuth } from "../features/authentication";
import { PermissionEnum } from "../features/permissions";
import { apiClient, getErrorMessage } from "@/features/shared";
import { useSnackbar } from "@/features/ui";

export const ChartOfAccountPreferences = () => {
  const { hasPermission } = useAuth();
  const { showSnackbar } = useSnackbar();
  const [activeTab, setActiveTab] = useState(
    AccountingActionCategory.GOODS_AND_SERVICES,
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState({});
  const [invalidActions, setInvalidActions] = useState(new Set());
  const canUpdate = hasPermission(
    PermissionEnum.UPDATE_ACCOUNTING_ACTION_CHART_OF_ACCOUNT_PREFERENCES,
  );

  const fetchPreferences = useCallback(async () => {
    try {
      setLoading(true);

      const { data } = await apiClient.get("accounting-action-preferences");

      // Group by action, collect full COA objects
      const preferencesMap = {};
      (data.data || []).forEach((item) => {
        if (!preferencesMap[item.action]) {
          preferencesMap[item.action] = [];
        }
        if (item.chartOfAccount) {
          preferencesMap[item.action].push(item.chartOfAccount);
        }
      });

      setPreferences(preferencesMap);
    } catch (error) {
      showSnackbar("Gagal memuat preferensi akun", "error");
      console.error("Error fetching preferences:", error);
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchPreferences();
  }, [fetchPreferences]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  const handleUpdatePreferences = (action, accounts) => {
    setPreferences((prev) => ({
      ...prev,
      [action]: accounts,
    }));

    // Clear the error highlight once a required action gets an account.
    setInvalidActions((prev) => {
      if (!prev.has(action) || accounts.length === 0) return prev;
      const next = new Set(prev);
      next.delete(action);
      return next;
    });
  };

  const handleSave = async () => {
    const missingActions = [...REQUIRED_ACTIONS].filter(
      (action) => !(preferences[action]?.length > 0),
    );

    if (missingActions.length > 0) {
      setInvalidActions(new Set(missingActions));
      setActiveTab(AccountingActionCategory.COMPANY);
      showSnackbar(
        `Akun berikut wajib diisi: ${missingActions
          .map((action) => AccountingActionLabel[action])
          .join(", ")}`,
        "error",
      );
      return;
    }

    try {
      setSaving(true);

      const payload = {
        preferences: Object.entries(preferences).map(([action, accounts]) => ({
          action,
          chartOfAccounts: accounts.map((account, index) => ({
            id: account.id,
            sortOrder: index,
          })),
        })),
      };

      await apiClient.put("accounting-action-preferences", payload);

      showSnackbar("Preferensi akun berhasil disimpan", "success");
      fetchPreferences();
    } catch (error) {
      showSnackbar(getErrorMessage(error, "Gagal menyimpan preferensi akun"));
      console.error("Error saving preferences:", error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PageContainer maxWidth="xl" breadcrumbs={[]}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            minHeight: 400,
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="xl" breadcrumbs={[]}>
      <Paper>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          sx={{
            borderBottom: 1,
            borderColor: "divider",
            px: 2,
          }}
        >
          <Tab
            label={
              AccountingActionCategoryLabel[
                AccountingActionCategory.GOODS_AND_SERVICES
              ]
            }
            value={AccountingActionCategory.GOODS_AND_SERVICES}
          />
          <Tab
            label={
              AccountingActionCategoryLabel[AccountingActionCategory.COMPANY]
            }
            value={AccountingActionCategory.COMPANY}
          />
          <Tab
            label={
              AccountingActionCategoryLabel[
                AccountingActionCategory.SALES_PURCHASE
              ]
            }
            value={AccountingActionCategory.SALES_PURCHASE}
          />
          <Tab
            label={
              AccountingActionCategoryLabel[AccountingActionCategory.INVENTORY]
            }
            value={AccountingActionCategory.INVENTORY}
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {activeTab === AccountingActionCategory.GOODS_AND_SERVICES && (
            <PreferenceTab
              sections={
                ActionSectionMapping[
                  AccountingActionCategory.GOODS_AND_SERVICES
                ]
              }
              preferences={preferences}
              onUpdatePreferences={handleUpdatePreferences}
              readOnly={!canUpdate}
              invalidActions={invalidActions}
            />
          )}
          {activeTab === AccountingActionCategory.COMPANY && (
            <PreferenceTab
              sections={ActionSectionMapping[AccountingActionCategory.COMPANY]}
              preferences={preferences}
              onUpdatePreferences={handleUpdatePreferences}
              readOnly={!canUpdate}
              invalidActions={invalidActions}
            />
          )}
          {activeTab === AccountingActionCategory.SALES_PURCHASE && (
            <PreferenceTab
              sections={
                ActionSectionMapping[AccountingActionCategory.SALES_PURCHASE]
              }
              preferences={preferences}
              onUpdatePreferences={handleUpdatePreferences}
              readOnly={!canUpdate}
              invalidActions={invalidActions}
            />
          )}
          {activeTab === AccountingActionCategory.INVENTORY && (
            <PreferenceTab
              sections={
                ActionSectionMapping[AccountingActionCategory.INVENTORY]
              }
              preferences={preferences}
              onUpdatePreferences={handleUpdatePreferences}
              readOnly={!canUpdate}
              invalidActions={invalidActions}
            />
          )}
        </Box>
      </Paper>

      {canUpdate && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Menyimpan..." : "Simpan Semua"}
          </Button>
        </Box>
      )}
    </PageContainer>
  );
};
